// Run Assessment: Details -> Capabilities -> Summary -> Review, on the same
// principles as Publish Training: one centred column per step, one input
// beneath the next, the stepper above and the bottom bar always in view.
//   Details       who and what it is for.
//   Capabilities  what it measures (from Capability Setup), and when it runs.
//   Summary       everything entered, each line editable in place.
//   Review        Elliott's draft: one Roleplay and one Quiz Question per
//                 capability, and the message the employee will see.
//
// Elliott writes the draft (POST …/assessments/draft) and Run Assessment saves
// it as edited (POST …/assessments): the audience sees it on their dashboard.
//
// From the moment Details is complete, the backend keeps what has been entered
// (PUT …/assessment-drafts/{id}, ./assessmentDrafts.ts), so the dashboard shows
// the Assessment as a card at Design and its Continue reopens the flow at the
// step it was left (`resume`). Running it, or discarding it, ends that.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { apiMessage, capabilityApi } from '../../../../../api/capability';
import {
  useCapabilityOrg,
  useRefreshAssessmentDrafts,
  useRefreshCapability
} from '../../../../../queries/useCapability';
import { useOrgAudienceOptions } from '../../Program/components/learning-lifecycle/useOrgMemberAttributes';
import type { SetupRole } from '../capabilitySetup';
import type { CapabilitySet } from '../sharedSets';
import type { SharedBuildPreset } from '../SharedSetView';
import { ActionButton, BODY, Chip, ChipLine, DANGER, DateField, FlowStep, FormRow, Stepper } from '../designSystem';
import { AssessmentDraftView, DraftWriting, MessageToEmployee } from './AssessmentDraftView';
import { draftMessage, type DraftItem } from './assessmentDraft';
import {
  detailsOfDraft,
  draftBody,
  draftOf,
  firstRunOfDraft,
  newDraftId,
  type AssessmentDraft
} from './assessmentDrafts';
import { audienceInput } from './audience';
import {
  DETAIL_FIELDS,
  DetailsFields,
  EMPTY_DETAILS,
  detailsMissing,
  stepBar,
  type DetailKey,
  type DetailsFieldProps,
  type FlowDetails,
  type SetDetail
} from './DetailsStep';
import { CapabilitiesField, ReviewFields, formatDate, listNames, rolesInScope, type SummaryLine } from './common';

type StepKey = 'details' | 'capabilities' | 'summary' | 'review';
const STEPS: { key: StepKey; label: string }[] = [
  { key: 'details', label: 'Details' },
  { key: 'capabilities', label: 'Capabilities' },
  { key: 'summary', label: 'Summary' },
  { key: 'review', label: 'Review' }
];

type Schedule = 'none' | 'monthly' | 'quarterly' | 'halfYearly' | 'yearly';
const SCHEDULES: { key: Schedule; label: string }[] = [
  { key: 'none', label: 'Once' },
  { key: 'monthly', label: 'Every Month' },
  { key: 'quarterly', label: 'Every Quarter' },
  { key: 'halfYearly', label: 'Every Six Months' },
  { key: 'yearly', label: 'Every Year' }
];

type CapabilityKey = 'capabilities' | 'schedule' | 'firstRun';
type SummaryKey = DetailKey | CapabilityKey;

const AssessmentFlow: React.FC<{
  pillars: { id: string; name: string }[];
  members: { id: string; name: string }[];
  /** Every role set up in Capability Setup. */
  roles: SetupRole[];
  /** The organization's shared capabilities (Development, Standards), applied to roles. */
  sets?: CapabilitySet[];
  teamsListId: string | null;
  onClose: () => void;
  /** Told once the Assessment is run, with the roles it measures. */
  onRun?: (roleIds: string[]) => void;
  /** An unfinished Assessment, reopened where it was left (Continue on its card). */
  resume?: AssessmentDraft | null;
  /** Opened from a shared set: its capabilities, and the roles they apply to, chosen to start with. */
  preset?: SharedBuildPreset;
}> = ({ pillars, members, roles, sets = [], teamsListId, onClose, onRun, resume = null, preset }) => {
  const org = useCapabilityOrg();
  const refresh = useRefreshCapability();
  const refreshDrafts = useRefreshAssessmentDrafts();
  const [step, setStep] = useState<StepKey>(resume?.step ?? 'details');
  // An Assessment measures capabilities, so it is always a Role Excellence program, for roles only.
  const [details, setDetails] = useState<FlowDetails>(() =>
    resume ? detailsOfDraft(resume) : { ...EMPTY_DETAILS, category: 'role-excellence', roleIds: preset?.roleIds ?? [] }
  );
  // null = every capability of the roles in scope, until the manager unticks one.
  const [capabilityIds, setCapabilityIds] = useState<string[] | null>(
    resume?.capabilityIds ?? preset?.capabilityIds ?? null
  );
  const [schedule, setSchedule] = useState<Schedule>(resume?.schedule ?? 'quarterly');
  // The clock starts on publish unless the manager names a date.
  const [startOnPublish, setStartOnPublish] = useState(resume?.startOnPublish ?? true);
  const [firstRun, setFirstRun] = useState<Date | null>(() => (resume ? firstRunOfDraft(resume) : null));
  // Elliott's draft, and the capabilities it was written for.
  const [draft, setDraft] = useState<{ key: string; items: DraftItem[] } | null>(resume?.written ?? null);
  const [message, setMessage] = useState<string | null>(resume?.message ?? null);
  const [writing, setWriting] = useState(false);
  // Why Elliott's draft or the run didn't go through, when they didn't.
  const [draftError, setDraftError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  const set: SetDetail = (key, value) => setDetails((d) => ({ ...d, [key]: value }));
  const at = STEPS.findIndex((s) => s.key === step);
  const stepper = <Stepper steps={STEPS.map((s) => s.label)} current={at} />;
  const { labelById, catalog } = useOrgAudienceOptions();

  // Each role with every capability it holds: its own, then the shared ones applied to it.
  const scope = rolesInScope('assessment', roles, details, sets);
  // A capability several roles in scope share is one capability (one id): drafted and counted once.
  const allCapabilities = scope
    .flatMap((r) => r.capabilities)
    .filter((c, i, list) => list.findIndex((x) => x.id === c.id) === i);
  const picked = (capabilityIds ?? allCapabilities.map((c) => c.id)).filter((id) =>
    allCapabilities.some((c) => c.id === id)
  );
  const pickedNames = allCapabilities.filter((c) => picked.includes(c.id)).map((c) => c.name);
  const pickedKey = picked.join('|');
  const scheduleLabel = (SCHEDULES.find((s) => s.key === schedule) ?? SCHEDULES[0]).label;
  const firstRunText = startOnPublish ? 'On publish' : formatDate(firstRun);
  const stageName = pillars.find((p) => p.id === details.stageId)?.name ?? null;
  const ownerName = members.find((m) => m.id === details.ownerId)?.name ?? null;
  const who = details.allLearners
    ? 'Everyone in the organization'
    : listNames([
        ...roles.filter((r) => details.roleIds.includes(r.id)).map((r) => r.name),
        ...details.audienceIds.map((id) => labelById.get(id) ?? '').filter(Boolean)
      ]);

  // Review: Elliott writes one Roleplay and one Quiz Question per capability. Written
  // again only when the capabilities chosen have changed; edits are kept otherwise.
  useEffect(() => {
    if (step !== 'review' || draft?.key === pickedKey) return undefined;
    let alive = true;
    setWriting(true);
    setDraftError(null);
    capabilityApi
      .draftAssessment(
        {
          name: details.name.trim(),
          category: details.category,
          audience: who,
          docIds: details.docIds,
          capabilityIds: picked,
          roleIds: details.roleIds
        },
        org
      )
      .then(({ items }) => {
        if (!alive) return;
        setDraft({ key: pickedKey, items });
        setMessage((current) => current ?? draftMessage(details.name, ownerName, items.length));
      })
      .catch((err) => alive && setDraftError(apiMessage(err, 'Elliott could not write the draft.')))
      .finally(() => alive && setWriting(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, pickedKey, attempt]);

  // Kept from the first time Details is left, then as each step is left and on closing part-way.
  // Running it, or discarding it, ends that.
  const keptId = useRef<string | null>(resume?.id ?? null);
  const ended = useRef(false);
  const latest = useRef({
    step,
    details,
    capabilityIds,
    schedule,
    startOnPublish,
    firstRun,
    written: draft,
    message,
    ownerName
  });
  latest.current = {
    step,
    details,
    capabilityIds,
    schedule,
    startOnPublish,
    firstRun,
    written: draft,
    message,
    ownerName
  };
  // Sent one after another, so an older save never lands on top of a newer one.
  const sending = useRef<Promise<unknown>>(Promise.resolve());
  const keep = useCallback(() => {
    if (!keptId.current || ended.current) return;
    const kept = draftOf({ ...latest.current, id: keptId.current });
    sending.current = sending.current
      .then(() => capabilityApi.saveAssessmentDraft(kept.id, draftBody(kept), org))
      .then(refreshDrafts)
      // Not kept this time (the backend refused it, or does not keep drafts yet): the flow carries on.
      .catch(() => undefined);
  }, [org, refreshDrafts]);
  // Not on opening: nothing has changed yet.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) keep();
    opened.current = true;
  }, [keep, step]);
  useEffect(() => keep, [keep]);
  const discard = () => {
    ended.current = true;
    const id = keptId.current;
    if (id) {
      sending.current = sending.current
        .then(() => capabilityApi.discardAssessmentDraft(id, org))
        .then(refreshDrafts)
        .catch((err) => toast.error(apiMessage(err, 'The draft could not be discarded. Try again from its card.')));
    }
    onClose();
  };

  const go = (key: StepKey) => {
    if (step === 'details' && !keptId.current) keptId.current = newDraftId();
    setStep(key);
  };
  // Bottom left of the bar, apart from the actions on the right.
  const previous = <ActionButton onClick={() => go(STEPS[at - 1].key)}>Previous</ActionButton>;
  const detailProps: DetailsFieldProps = {
    type: 'assessment',
    details,
    set,
    pillars,
    members,
    roles,
    teamsListId,
    sets
  };
  const continueTo = (key: StepKey, enabled = true) => (
    <ActionButton primary disabled={!enabled} reason="Fill in the required inputs first." onClick={() => go(key)}>
      Next
    </ActionButton>
  );

  const run = async () => {
    if (!draft || running) return;
    const measured = Array.from(new Set(draft.items.map((i) => i.roleId)));
    setRunning(true);
    setRunError(null);
    try {
      // Whatever is still being kept lands first: the run forgets the draft, and nothing may bring it back.
      await sending.current;
      await capabilityApi.runAssessment(
        {
          name: details.name.trim(),
          stageId: details.stageId,
          category: details.category,
          ownerId: details.ownerId,
          publishBy: details.publishBy ? details.publishBy.toISOString() : null,
          docIds: details.docIds,
          audienceLabel: who,
          audience: audienceInput(details, catalog),
          schedule,
          firstRun: startOnPublish || !firstRun ? null : firstRun.toISOString(),
          message: message ?? '',
          items: draft.items.map((i) => ({ capabilityId: i.capabilityId, roleplay: i.roleplay, quiz: i.quiz }))
        },
        org,
        keptId.current ?? undefined
      );
    } catch (err) {
      setRunError(apiMessage(err, 'The Assessment could not be run. Try again.'));
      setRunning(false);
      return;
    }
    // It is run: no longer one being built (the backend has forgotten the draft).
    ended.current = true;
    refresh('all');
    onRun?.(measured);
    toast.success(`${details.name.trim()} is published.`);
    onClose();
  };

  // ---- The inputs of Capabilities, each on its own so Summary can open one to edit.
  const capabilitiesField = (
    <CapabilitiesField
      roles={scope}
      picked={picked}
      onChange={setCapabilityIds}
      tip="What the Assessment measures, from the roles in its audience. Each gets one Roleplay and one Quiz Question, graded against its four levels."
    />
  );
  const scheduleField = (
    <FormRow
      required
      label="Schedule"
      tip="How often it runs. A repeat shows how each person's level changes over time."
    >
      <ChipLine>
        {SCHEDULES.map((s) => (
          <Chip key={s.key} label={s.label} selected={schedule === s.key} onClick={() => setSchedule(s.key)} />
        ))}
      </ChipLine>
    </FormRow>
  );
  const firstRunField = (
    <FormRow required label="First run" tip="When it first goes out. Later runs follow the schedule from this date.">
      <ChipLine>
        <Chip label="On Publish" selected={startOnPublish} onClick={() => setStartOnPublish(true)} />
        <Chip label="On a Date" selected={!startOnPublish} onClick={() => setStartOnPublish(false)} />
        {!startOnPublish && <DateField label="First run date" value={firstRun} onChange={setFirstRun} width={190} />}
      </ChipLine>
    </FormRow>
  );

  // ---- Details
  if (step === 'details') {
    const bar = stepBar('Details', detailsMissing(details));
    return (
      <FlowStep
        stepper={stepper}
        bar={{
          ...bar,
          actions: (
            <>
              {keptId.current && <ActionButton onClick={discard}>Discard Draft</ActionButton>}
              <ActionButton onClick={onClose}>{keptId.current ? 'Close' : 'Cancel'}</ActionButton>
              {continueTo('capabilities', bar.done)}
            </>
          )
        }}
      >
        <DetailsFields {...detailProps} />
      </FlowStep>
    );
  }

  // ---- Capabilities
  if (step === 'capabilities') {
    const bar = stepBar(
      'Capabilities',
      [picked.length === 0 && 'Capabilities', !startOnPublish && !firstRun && 'First run'].filter((m): m is string =>
        Boolean(m)
      )
    );
    return (
      <FlowStep
        stepper={stepper}
        bar={{
          ...bar,
          previous,
          actions: continueTo('summary', bar.done)
        }}
      >
        {capabilitiesField}
        {scheduleField}
        {firstRunField}
      </FlowStep>
    );
  }

  // ---- Summary
  if (step === 'summary') {
    const lines: SummaryLine<SummaryKey>[] = [
      { key: 'name', label: 'Name', value: details.name.trim() || null },
      { key: 'stage', label: 'Stage', value: stageName },
      { key: 'audience', label: 'Audience', value: who || null },
      { key: 'owner', label: 'Owner', value: ownerName },
      { key: 'owner', label: 'Publish by', value: formatDate(details.publishBy) ?? 'No date' },
      {
        key: 'knowledge',
        label: 'Knowledge',
        value: details.docIds.length ? `${details.docIds.length} sources` : 'No sources'
      },
      { key: 'capabilities', label: 'Capabilities', value: pickedNames.length ? pickedNames.join(', ') : null },
      { key: 'schedule', label: 'Schedule', value: scheduleLabel },
      { key: 'firstRun', label: 'First run', value: firstRunText }
    ];
    const renderEditor = (key: SummaryKey) => {
      if (key === 'capabilities') return capabilitiesField;
      if (key === 'schedule') return scheduleField;
      if (key === 'firstRun') return firstRunField;
      const FieldComponent = DETAIL_FIELDS[key];
      return <FieldComponent {...detailProps} />;
    };
    // An edit made here can leave something required empty again.
    const complete = detailsMissing(details).length === 0 && picked.length > 0 && (startOnPublish || Boolean(firstRun));
    return (
      <FlowStep
        stepper={stepper}
        bar={{
          done: complete,
          title: complete ? 'Summary complete' : 'A required input is empty',
          body: complete
            ? `Next: Elliott drafts ${
                picked.length === 1
                  ? 'a Roleplay and a Quiz Question'
                  : `${picked.length} Roleplays and ${picked.length} Quiz Questions`
              } for you to review.`
            : 'Edit the line marked Not set.',
          previous,
          actions: continueTo('review', complete)
        }}
      >
        <ReviewFields lines={lines} renderEditor={renderEditor} />
      </FlowStep>
    );
  }

  // ---- Review: Elliott's draft, then the message the employee sees.
  const ready = !writing && draft?.key === pickedKey;
  const failed = !writing && Boolean(draftError);
  return (
    <FlowStep
      stepper={stepper}
      bar={{
        done: ready && !runError,
        title: failed
          ? "Elliott couldn't write the draft"
          : runError
          ? "The Assessment didn't run"
          : ready
          ? 'Ready to run'
          : 'Elliott is writing the draft',
        body: failed
          ? draftError ?? undefined
          : runError ??
            (ready
              ? `${who || 'The audience'} will see this Assessment on their Dashboard, with your message.`
              : undefined),
        previous,
        actions: (
          <>
            {failed ? (
              <ActionButton primary onClick={() => setAttempt((n) => n + 1)}>
                Try Again
              </ActionButton>
            ) : (
              <ActionButton
                primary
                disabled={!ready || running}
                reason={running ? 'Running it now.' : 'One moment.'}
                onClick={run}
              >
                {running ? 'Running...' : 'Build Assessment'}
              </ActionButton>
            )}
          </>
        )
      }}
    >
      {ready && draft ? (
        <>
          <AssessmentDraftView items={draft.items} onChange={(items) => setDraft({ key: draft.key, items })} />
          <MessageToEmployee value={message ?? ''} onChange={setMessage} />
        </>
      ) : failed ? (
        <Typography role="alert" sx={{ ...BODY, color: DANGER }}>
          {draftError}
        </Typography>
      ) : (
        <DraftWriting />
      )}
    </FlowStep>
  );
};

export default AssessmentFlow;
